// rename inputs to args
// append a "response" to response types

import { ApiResponse } from 'lib-shared-types-api';

import { CustomFormApiTypes } from './customForms.types';

export type CreateCustomFormArg = {
  /** Title of the form */
  readonly label: string;
  /** Description of the form */
  readonly description: string;
  /** Label of the submit button */
  readonly submitButtonLabel: string;
  /** Feedback message to show after the form has been submitted */
  readonly submitSuccessMessage: string;
};

export type CreateCustomFormResponse = ApiResponse<CustomFormApiTypes['Form']>;

// ─────────────────────────────────────────────────────────────────────────────

export type DeleteCustomFormPathArg = {
  /** Id of the form to delete. */
  readonly form_id: string;
};

/** Returns 204 No Content on success (the response payload is empty) */
export type DeleteCustomFormResponse = void;

// ─────────────────────────────────────────────────────────────────────────────

export type DuplicateCustomFormArg = {
  /** Id of the form to duplicate. */
  readonly form_id: string;
  /** Title of the form */
  readonly label?: string;
  /** Description of the form */
  readonly description?: string;
  /** A unique identifier (UUID) for the new form. */
  readonly identifier: string;
};

export type DuplicateCustomFormResponse = ApiResponse<
  CustomFormApiTypes['Form']
>;

// ─────────────────────────────────────────────────────────────────────────────

export type ReadCustomFormArg = {
  /** Id of the form to read. */
  readonly form_id: string;
  /** Version of the form to read. Defaults to the latest */
  readonly version?: string;
};

export type ReadCustomFormResponse = ApiResponse<CustomFormApiTypes['Form']>;

// ─────────────────────────────────────────────────────────────────────────────

export type ReadCustomFormsArg = void;

export type ReadCustomFormsResponse = ApiResponse<{
  readonly forms: CustomFormApiTypes['FormsList'];
}>;

// ─────────────────────────────────────────────────────────────────────────────

export enum CustomFormValidationKind {
  FileMediaType = 'file_media_type',
  FileMaxSize = 'file_max_size',
  FilesTotalMaxSize = 'files_total_max_size',
  Required = 'required',
  MaxLength = 'max_length',
  MinLength = 'min_length',
  CustomFeel = 'custom_feel',
  Email = 'email',
  Regexp = 'regexp',
}

interface CustomFormValidation {
  readonly id: string;
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind;
  };
}

export interface CustomFormValidationFileMediaType extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.FileMediaType;
    readonly allowed: string[];
  };
}

export interface CustomFormValidationFileMaxSize extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.FileMaxSize;
    readonly max_byte_length: number;
  };
}

export interface CustomFormValidationFilesTotalMaxSize extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.FilesTotalMaxSize;
    readonly max_byte_length: number;
  };
}

export interface CustomFormValidationRequired extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.Required;
  };
}

export interface CustomFormValidationMaxLength extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.MaxLength;
    readonly max_length: number;
  };
}

export interface CustomFormValidationMinLength extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.MinLength;
    readonly min_length: number;
  };
}

export interface CustomFormValidationCustomFeel extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.CustomFeel;
    readonly expression: string;
  };
}

export interface CustomFormValidationEmail extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.Email;
  };
}

export interface CustomFormValidationRegexp extends CustomFormValidation {
  readonly fn: {
    readonly attribute_id: string;
    readonly kind: CustomFormValidationKind.Regexp;
    readonly pattern: string;
  };
}

export type CustomFormValidationTypes =
  | CustomFormValidationFileMediaType
  | CustomFormValidationFileMaxSize
  | CustomFormValidationFilesTotalMaxSize
  | CustomFormValidationRequired
  | CustomFormValidationMaxLength
  | CustomFormValidationMinLength
  | CustomFormValidationCustomFeel
  | CustomFormValidationEmail
  | CustomFormValidationRegexp;

export type UpdateCustomFormArg = Omit<
  CustomFormApiTypes['Form'],
  | 'organization_id'
  | 'created_at'
  | 'updated_at'
  | 'attributes'
  | 'label'
  | 'description'
  | 'submit_button_label'
  | 'submit_success_message'
> & {
  /** List of form fields */
  readonly attributes?: FormUpdateAttribute[];
  readonly validations?: CustomFormValidationTypes[];
};

export type FormUpdateAttribute = Omit<
  CustomFormApiTypes['FormAttribute'],
  | 'created_at'
  | 'updated_at'
  | 'organization_id'
  | 'options'
  | 'label'
  | 'description'
  | 'place_holder'
> & {
  /** List of form field options */
  readonly options?: FormUpdateSelectOptions[];
};

export type FormUpdateSelectOptions = Omit<
  CustomFormApiTypes['FormAttributeOption'],
  'sub_options' | 'label'
> & { readonly sub_options: FormUpdateSelectOptions[] };

export type UpdateCustomFormResponse = ApiResponse<CustomFormApiTypes['Form']>;

// ─────────────────────────────────────────────────────────────────────────────

export type ReadFormSubmissionArg = {
  /** Id of the form submission to read. */
  readonly submission_id: string;
  /** Id of the form that the submission belongs to. */
  readonly form_id: string;
  /** Version of the form to read. */
  readonly form_version: string;
};

export type ReadFormSubmissionResponse = ApiResponse<
  CustomFormApiTypes['FormSubmission']
>;

// ─────────────────────────────────────────────────────────────────────────────

export type BadRequestError = {
  /** Error message parsed from the response */
  readonly error: string;
};
